import React, { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  User,
  Mail,
  Phone,
  Shield,
  Building2,
  Users,
  ArrowUpRight,
  Bell,
  Lock,
  Globe,
  Upload,
  CheckCircle,
  AlertCircle,
  Camera,
  FileText,
  Home,
  RefreshCw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user, upgradeToSME, checkAuth } = useAuth();
  const [kycStatus, setKycStatus] = useState(null);
  const [kycLoading, setKycLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const [emailTestLoading, setEmailTestLoading] = useState(false);

  const [kycForm, setKycForm] = useState({
    id_number: "",
    date_of_birth: "",
    address_line_1: "",
    address_city: "",
    address_country: "KE"
  });

  useEffect(() => {
    fetchKycStatus();
  }, []);

  const fetchKycStatus = async () => {
    try {
      const response = await axios.get(`${API}/kyc/status`, { withCredentials: true });
      setKycStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch KYC status:", error);
    } finally {
      setKycLoading(false);
    }
  };

  const handleUpgradeToSME = async () => {
    try {
      await upgradeToSME();
      toast.success("Upgraded to SME account!");
    } catch (error) {
      toast.error("Failed to upgrade account");
    }
  };

  const handleFileUpload = async (documentType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, WebP, or PDF file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploadLoading(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(",")[1];
        
        await axios.post(
          `${API}/kyc/document`,
          {
            document_type: documentType,
            file_data: base64,
            file_name: file.name
          },
          { withCredentials: true }
        );

        toast.success(`${documentType} document uploaded successfully`);
        fetchKycStatus();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleKycSubmit = async (e) => {
    e.preventDefault();
    setVerifyLoading(true);

    try {
      await axios.post(`${API}/kyc/verify`, kycForm, { withCredentials: true });
      toast.success("KYC verification completed!");
      setKycDialogOpen(false);
      fetchKycStatus();
      checkAuth(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit verification");
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setEmailTestLoading(true);
    try {
      const response = await axios.post(`${API}/notifications/email/test`, {}, { withCredentials: true });
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to send test email");
    } finally {
      setEmailTestLoading(false);
    }
  };

  const getKycProgress = () => {
    if (!kycStatus) return 0;
    return (kycStatus.verification_level / 4) * 100;
  };

  const getKycStatusColor = () => {
    switch (kycStatus?.kyc_status) {
      case "verified": return "bg-green-100 text-green-700";
      case "in_review": return "bg-amber-100 text-amber-700";
      case "rejected": return "bg-red-100 text-red-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in max-w-4xl" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-600 mt-1">Manage your account preferences</p>
        </div>

        {/* Profile Card */}
        <Card className="border-slate-200" data-testid="profile-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-6">
              <Avatar className="h-20 w-20">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#14B8A6] text-white text-2xl">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{user?.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="capitalize">
                      {user?.account_type === "sme" ? (
                        <>
                          <Building2 className="h-3 w-3 mr-1" />
                          SME Account
                        </>
                      ) : (
                        <>
                          <Users className="h-3 w-3 mr-1" />
                          Consumer Account
                        </>
                      )}
                    </Badge>
                    {user?.kyc_verified && (
                      <Badge className="bg-green-100 text-green-700">
                        <Shield className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="h-4 w-4" />
                    {user?.email}
                  </div>
                  {user?.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="h-4 w-4" />
                      {user?.phone}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KYC Verification Card */}
        <Card className="border-slate-200" data-testid="kyc-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#14B8A6]" />
              KYC Verification
            </CardTitle>
          </CardHeader>
          <CardContent>
            {kycLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-[#14B8A6]" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Status Overview */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Verification Status</p>
                    <Badge className={`mt-1 ${getKycStatusColor()}`}>
                      {kycStatus?.kyc_status === "verified" && <CheckCircle className="h-3 w-3 mr-1" />}
                      {kycStatus?.kyc_status === "in_review" && <AlertCircle className="h-3 w-3 mr-1" />}
                      {kycStatus?.kyc_status?.toUpperCase() || "PENDING"}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Level</p>
                    <p className="text-2xl font-bold text-slate-900">{kycStatus?.verification_level || 0}/4</p>
                  </div>
                </div>

                <Progress value={getKycProgress()} className="h-2" />

                {/* Document Upload Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Identity Document */}
                  <div className={`p-4 rounded-xl border-2 border-dashed ${
                    kycStatus?.documents_submitted?.includes("identity")
                      ? "border-green-300 bg-green-50"
                      : "border-slate-200"
                  }`}>
                    <div className="text-center">
                      <FileText className={`h-8 w-8 mx-auto mb-2 ${
                        kycStatus?.documents_submitted?.includes("identity")
                          ? "text-green-600"
                          : "text-slate-400"
                      }`} />
                      <p className="font-medium text-slate-900">ID Document</p>
                      <p className="text-xs text-slate-500 mt-1">Passport or National ID</p>
                      {kycStatus?.documents_submitted?.includes("identity") ? (
                        <Badge className="mt-2 bg-green-100 text-green-700">Uploaded</Badge>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileUpload("identity", e)}
                            disabled={uploadLoading}
                          />
                          <Button size="sm" variant="outline" className="mt-2 rounded-full" disabled={uploadLoading}>
                            <Upload className="h-4 w-4 mr-1" />
                            Upload
                          </Button>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Selfie */}
                  <div className={`p-4 rounded-xl border-2 border-dashed ${
                    kycStatus?.documents_submitted?.includes("selfie")
                      ? "border-green-300 bg-green-50"
                      : "border-slate-200"
                  }`}>
                    <div className="text-center">
                      <Camera className={`h-8 w-8 mx-auto mb-2 ${
                        kycStatus?.documents_submitted?.includes("selfie")
                          ? "text-green-600"
                          : "text-slate-400"
                      }`} />
                      <p className="font-medium text-slate-900">Selfie</p>
                      <p className="text-xs text-slate-500 mt-1">Take a clear photo</p>
                      {kycStatus?.documents_submitted?.includes("selfie") ? (
                        <Badge className="mt-2 bg-green-100 text-green-700">Uploaded</Badge>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => handleFileUpload("selfie", e)}
                            disabled={uploadLoading}
                          />
                          <Button size="sm" variant="outline" className="mt-2 rounded-full" disabled={uploadLoading}>
                            <Upload className="h-4 w-4 mr-1" />
                            Upload
                          </Button>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Address Proof */}
                  <div className={`p-4 rounded-xl border-2 border-dashed ${
                    kycStatus?.documents_submitted?.includes("address")
                      ? "border-green-300 bg-green-50"
                      : "border-slate-200"
                  }`}>
                    <div className="text-center">
                      <Home className={`h-8 w-8 mx-auto mb-2 ${
                        kycStatus?.documents_submitted?.includes("address")
                          ? "text-green-600"
                          : "text-slate-400"
                      }`} />
                      <p className="font-medium text-slate-900">Address Proof</p>
                      <p className="text-xs text-slate-500 mt-1">Utility bill or bank statement</p>
                      {kycStatus?.documents_submitted?.includes("address") ? (
                        <Badge className="mt-2 bg-green-100 text-green-700">Uploaded</Badge>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileUpload("address", e)}
                            disabled={uploadLoading}
                          />
                          <Button size="sm" variant="outline" className="mt-2 rounded-full" disabled={uploadLoading}>
                            <Upload className="h-4 w-4 mr-1" />
                            Upload
                          </Button>
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                {/* Submit Verification Button */}
                {kycStatus?.documents_submitted?.includes("identity") && 
                 kycStatus?.documents_submitted?.includes("selfie") &&
                 kycStatus?.kyc_status !== "verified" && (
                  <Dialog open={kycDialogOpen} onOpenChange={setKycDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full rounded-full bg-[#14B8A6] hover:bg-[#0D9488]" data-testid="complete-kyc-btn">
                        <Shield className="h-4 w-4 mr-2" />
                        Complete Verification
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Complete KYC Verification</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleKycSubmit} className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <Label>ID Number</Label>
                          <Input
                            placeholder="Enter your ID number"
                            value={kycForm.id_number}
                            onChange={(e) => setKycForm({ ...kycForm, id_number: e.target.value })}
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Date of Birth</Label>
                          <Input
                            type="date"
                            value={kycForm.date_of_birth}
                            onChange={(e) => setKycForm({ ...kycForm, date_of_birth: e.target.value })}
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Address</Label>
                          <Input
                            placeholder="Street address"
                            value={kycForm.address_line_1}
                            onChange={(e) => setKycForm({ ...kycForm, address_line_1: e.target.value })}
                            className="h-12"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>City</Label>
                            <Input
                              placeholder="City"
                              value={kycForm.address_city}
                              onChange={(e) => setKycForm({ ...kycForm, address_city: e.target.value })}
                              className="h-12"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Country</Label>
                            <Select
                              value={kycForm.address_country}
                              onValueChange={(v) => setKycForm({ ...kycForm, address_country: v })}
                            >
                              <SelectTrigger className="h-12">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="KE">🇰🇪 Kenya</SelectItem>
                                <SelectItem value="UG">🇺🇬 Uganda</SelectItem>
                                <SelectItem value="TZ">🇹🇿 Tanzania</SelectItem>
                                <SelectItem value="RW">🇷🇼 Rwanda</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button
                          type="submit"
                          className="w-full rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                          disabled={verifyLoading}
                        >
                          {verifyLoading ? (
                            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2" />
                          )}
                          Submit Verification
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}

                {kycStatus?.kyc_status === "verified" && (
                  <div className="p-4 bg-green-50 rounded-xl text-center">
                    <CheckCircle className="h-8 w-8 mx-auto text-green-600 mb-2" />
                    <p className="font-medium text-green-800">Your identity is verified!</p>
                    <p className="text-sm text-green-600">You have full access to all OnePay features.</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Account Upgrade */}
        {user?.account_type === "consumer" && (
          <Card className="border-[#14B8A6] border-2" data-testid="upgrade-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-[#14B8A6]" />
                    Upgrade to SME Account
                  </h3>
                  <p className="text-sm text-slate-600">
                    Unlock higher credit limits, supplier payments, and business features.
                  </p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Working capital up to $500</li>
                    <li>• Cross-border supplier payments</li>
                    <li>• Transaction tagging & reports</li>
                  </ul>
                </div>
                <Button
                  className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                  onClick={handleUpgradeToSME}
                  data-testid="upgrade-btn"
                >
                  <ArrowUpRight className="h-4 w-4 mr-2" />
                  Upgrade Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Notifications */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { label: "Transaction Alerts", desc: "Get notified for every transaction" },
                { label: "FX Rate Updates", desc: "Daily rate summaries" },
                { label: "Credit Reminders", desc: "Repayment due date alerts" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-slate-900">{item.label}</p>
                    <p className="text-xs text-slate-500">{item.desc}</p>
                  </div>
                  <div className="h-6 w-10 bg-[#14B8A6] rounded-full relative cursor-pointer">
                    <div className="absolute right-1 top-1 h-4 w-4 bg-white rounded-full"></div>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                className="w-full rounded-xl"
                onClick={handleTestEmail}
                disabled={emailTestLoading}
              >
                {emailTestLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Send Test Email
              </Button>
            </CardContent>
          </Card>

          {/* Security */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" className="w-full justify-start rounded-xl h-12">
                <Lock className="h-4 w-4 mr-3" />
                Change Password
              </Button>
              <Button variant="outline" className="w-full justify-start rounded-xl h-12">
                <Shield className="h-4 w-4 mr-3" />
                Two-Factor Authentication
              </Button>
              <Button variant="outline" className="w-full justify-start rounded-xl h-12">
                <User className="h-4 w-4 mr-3" />
                Manage Sessions
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Preferences */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Preferences
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-900">Default Currency</label>
                <select className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm">
                  <option value="USD">USD - US Dollar</option>
                  <option value="KES">KES - Kenyan Shilling</option>
                  <option value="UGX">UGX - Ugandan Shilling</option>
                  <option value="TZS">TZS - Tanzanian Shilling</option>
                  <option value="RWF">RWF - Rwandan Franc</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-900">Language</label>
                <select className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm">
                  <option value="en">English</option>
                  <option value="sw">Swahili</option>
                  <option value="fr">French</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Subscription</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <p className="font-semibold text-slate-900">Free Plan</p>
                <p className="text-sm text-slate-500">Basic features with limited FX conversions</p>
              </div>
              <Button className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488]" data-testid="upgrade-plan-btn">
                Upgrade Plan
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-red-700">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Delete Account</p>
                <p className="text-sm text-slate-500">Permanently delete your account and all data</p>
              </div>
              <Button variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-100">
                Delete Account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
